import { useState, useEffect } from 'react';
import { Search, Trash2, Settings } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { UserPermissionEditor } from './UserPermissionEditor';
import { toast } from 'sonner';

interface User {
  id: string;
  username: string;
  role: string;
  permissionGroups: string[];
  createdAt: number;
}

export function UserManagement() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [permissionFilter, setPermissionFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
      });

      if (search) params.append('search', search);
      if (permissionFilter && permissionFilter !== 'all') params.append('permission', permissionFilter);

      const response = await fetch(`/api/admin/users?${params}`, {
        credentials: 'include',
      });
      const data = await response.json();

      if (data.success && data.data) {
        setUsers(data.data.users || []);
        setTotalPages(data.data.pagination?.totalPages || 1);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('加载用户列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [page, search, permissionFilter]);

  const handleEditPermissions = (user: User) => {
    setEditingUser(user);
    setIsEditorOpen(true);
  };

  const handleEditorClose = () => {
    setIsEditorOpen(false);
    setEditingUser(null);
    fetchUsers(); // 重新加载用户列表
  };

  const handleDeleteUser = async (userId: string) => {
    if (!confirm('确定要删除这个用户吗?')) return;

    try {
      const response = await fetch(`/api/admin/users?userId=${userId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success('用户已删除');
        fetchUsers();
      } else {
        toast.error(data.error?.message || '删除失败');
      }
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast.error('删除用户失败');
    }
  };

  const getPermissionBadges = (groups: string[]) => {
    const colors: Record<string, string> = {
      basic: 'bg-secondary text-secondary-foreground',
      premium: 'bg-blue-500 text-white',
      contributor: 'bg-green-500 text-white',
      admin: 'bg-destructive text-destructive-foreground',
    };

    return (
      <div className="flex flex-wrap gap-1">
        {groups.map(group => (
          <span
            key={group}
            className={`px-2 py-0.5 rounded-full text-xs ${colors[group] || colors.basic}`}
          >
            {group}
          </span>
        ))}
      </div>
    );
  };

  const getRoleBadge = (role: string) => {
    return (
      <span className={`px-2 py-1 rounded-full text-xs ${
        role === 'admin'
          ? 'bg-destructive text-destructive-foreground'
          : 'bg-muted text-muted-foreground'
      }`}>
        {role === 'admin' ? '管理员' : '普通用户'}
      </span>
    );
  };

  return (
    <Card className="relative">
      <CardHeader>
        <CardTitle>用户管理</CardTitle>
      </CardHeader>
      <CardContent>
        {/* 搜索和筛选 */}
        <div className="flex gap-4 mb-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="搜索用户名..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={permissionFilter} onValueChange={setPermissionFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="筛选权限" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">全部</SelectItem>
              <SelectItem value="basic">Basic</SelectItem>
              <SelectItem value="premium">Premium</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* 用户列表 */}
        {loading ? (
          <div className="text-center py-8">加载中...</div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[100px]">ID</TableHead>
                  <TableHead>用户名</TableHead>
                  <TableHead>角色</TableHead>
                  <TableHead>权限组</TableHead>
                  <TableHead>注册时间</TableHead>
                  <TableHead className="text-right">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="font-mono text-xs">{user.id.slice(0, 8)}...</TableCell>
                    <TableCell className="font-medium">{user.username}</TableCell>
                    <TableCell>{getRoleBadge(user.role)}</TableCell>
                    <TableCell>{getPermissionBadges(user.permissionGroups)}</TableCell>
                    <TableCell>{new Date(user.createdAt * 1000).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditPermissions(user)}
                        >
                          <Settings className="h-4 w-4 mr-2" />
                          编辑权限
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDeleteUser(user.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* 分页 */}
            <div className="flex justify-center gap-2 mt-4">
              <Button
                variant="outline"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                上一页
              </Button>
              <span className="flex items-center px-4">
                第 {page} / {totalPages} 页
              </span>
              <Button
                variant="outline"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                下一页
              </Button>
            </div>
          </>
        )}
      </CardContent>

      {/* 权限编辑器对话框 */}
      <UserPermissionEditor
        user={editingUser}
        isOpen={isEditorOpen}
        onClose={handleEditorClose}
      />
    </Card>
  );
}

